import { ANALYTICS_NAVIGATION_MAP } from '@common/constants';
import {
  CollabSnapshotResponse,
  TeamsGroupsAdoptionSnapshotResponse,
} from '@hooks/adoption/types';
import React from 'react';
import styles from './styles.module.css';
import { DeviceAdoption } from '@pages/Dashboard/tiles/DeviceAdoption';
import { MeetingAdoption } from '@pages/Dashboard/tiles/MeetingAdoption';
import { InactiveRooms } from '@pages/Dashboard/tiles/InactiveRooms';
import { MeetingRoomVideo } from '@pages/Dashboard/tiles/MeetingRoomVideo';
import { MeetingRoomsHoursBooked } from '@pages/Dashboard/tiles/MeetingRoomsHoursBooked';
import { MsTeamsHealth } from '@pages/Dashboard/tiles/MsTeamsHealth';
import { OrgGroupActivityUser } from '@pages/Dashboard/tiles/OrgGroupActivityUser';
import { ServiceAdoptionByUser } from '@pages/Dashboard/tiles/ServiceAdoptionByUser';
import { AdoptionServiceUsageTrendOverTime } from '@pages/Dashboard/tiles/AdoptionServiceUsageTrendOverTime';
import { TeamsAndGroupsActivitySnapshot } from '@pages/Dashboard/tiles/TeamsAndGroupsActivitySnapshot';
import { TeamsAndGroupsAdoption } from '@pages/Dashboard/tiles/TeamsAndGroupsAdoption';
import { TeamsAndGroupsCollaboration } from '@pages/Dashboard/tiles/TeamsAndGroupsCollaboration';
import { UserServiceAdoptionSnapshot } from '@pages/Dashboard/tiles/UserServiceAdoptionSnapshot';
import { useAdoptionSummaryQuery, useFreshnessIndicatorEnabled } from '@hooks/summary';
import { getLatestUpdateTime, formatFreshnessTimestamp } from '@hooks/summary/freshness';

export enum TeamGroupType {
  Adoption,
  Collaboration,
}

export const getTeamsGroupsDataLabels = (
  data: TeamsGroupsAdoptionSnapshotResponse | undefined
) => {
  if (!data) {
    return [];
  }

  const props: (keyof TeamsGroupsAdoptionSnapshotResponse)[] = [
    'notInTeam',
    'inTeam',
  ];
  const labels = ['people not in a team', 'people in a team'];

  return props.map((i, index) => {
    return `${data[i] || 0} ${labels[index]}`;
  });
};

export const getCollabDataLabels = (
  data: CollabSnapshotResponse | undefined
) => {
  if (!data) {
    return [];
  }

  const props: (keyof CollabSnapshotResponse)[] = [
    'notCollaborate',
    'collaborate',
  ];
  const labels = ['people not collaborating', 'people collaborating'];

  return props.map((i, index) => {
    return `${data[i] || 0} ${labels[index]}`;
  });
};

const AdoptionsPage: React.FC = () => {
  const showFreshness = useFreshnessIndicatorEnabled();
  const { data: summaryData } = useAdoptionSummaryQuery();
  const freshnessTime = formatFreshnessTimestamp(getLatestUpdateTime(summaryData));

  return (
    <>
      {showFreshness && freshnessTime && (
        <p className="mb-2 text-right text-xs text-gray-400">Data as of {freshnessTime}</p>
      )}
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          <AdoptionServiceUsageTrendOverTime
            classNames="col-span-full lg:col-span-6 xl:col-span-6"
            params={JSON.stringify({ days: '7', serviceType: 'messaging' })}
          />

          <UserServiceAdoptionSnapshot
            params={JSON.stringify({ days: '7', dataType: 'messages' })}
          />

          <DeviceAdoption
            params={JSON.stringify({ days: '30', dataType: 'all' })}
          />
        </div>
      </div>

      <h1 className={styles.header}>Meetings & rooms</h1>
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          <MeetingAdoption params={JSON.stringify({ days: '30' })} />

          <MeetingRoomsHoursBooked
            classNames="col-span-full sm:col-span-6 lg:col-span-3 xl:col-span-4"
            params={JSON.stringify({ days: '30', dataType: 'top' })}
          />

          <MeetingRoomVideo
            params={JSON.stringify({ days: '30', roomId: 'allrooms' })}
          />

          <InactiveRooms params={JSON.stringify({ days: '30' })} />
        </div>
      </div>

      <h1 className={styles.header}>Teams & groups</h1>
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          <TeamsAndGroupsAdoption
            params={JSON.stringify({ vendorType: 'All', days: '30' })}
          />

          <TeamsAndGroupsCollaboration
            params={JSON.stringify({ days: '30' })}
          />

          <TeamsAndGroupsActivitySnapshot
            classNames="col-span-6 md:col-span-3 xl:col-span-2"
            params={JSON.stringify({ days: '30', dataType: 'allactivity' })}
          />

          <OrgGroupActivityUser
            params={JSON.stringify({ days: '30', dataType: 'allactivity' })}
          />

          <MsTeamsHealth
            params={JSON.stringify({ days: '30', dataType: 'membership' })}
          />

          <ServiceAdoptionByUser
            params={JSON.stringify({ serviceType: 'allservices', days: '30' })}
          />
        </div>
      </div>
    </>
  );
};

export default AdoptionsPage;
